import { useCallback, useMemo, useState } from "react";
import { Show } from "shared/components";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { useAuthContext, useVoiceEnabled } from "providers/Auth";
import { composerTabs } from "../components/constants";
import { ChatDisabledTooltip } from "../components/ChatDisabledTooltip";
import { hasOutstandingInvite, useEligibleApplications } from "../api/hooks";
import { useCrmContact, useCrmLeadDetails } from "../api/crm";
import type { EligibleApplication } from "../types";

const updatedAtMs = (app: EligibleApplication): number => {
  // "" timestamp → NaN; treat invalid as oldest to keep the sort defined.
  const ms = new Date(app.application_updated_at).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

const mostRecentApplicationId = (
  apps: EligibleApplication[] | undefined
): number | undefined => {
  if (!apps?.length) return undefined;
  return [...apps].sort((a, b) => updatedAtMs(b) - updatedAtMs(a))[0]
    .application_id;
};

// Shared composer tab list + chat gating for the message composer and the
// manual-communication form, so the Chat tab behaves identically on both.
export const useComposerTabs = (
  contactId?: string,
  options?: {
    inviteModalZIndex?: number;
    /**
     * Lead the host was opened from (the pop-up on a lead page). Invite-to-apply
     * then pre-selects that lead's community/unit and links the application to
     * it — the same props the lead page's own Invite button passes.
     */
    inviteLeadId?: number;
  }
) => {
  const { type } = useAuthContext();
  const isVoiceEnabled = useVoiceEnabled();

  const chatTabAvailable = type.isNativeChatActive;
  const { data: eligibleApplications } = useEligibleApplications(
    chatTabAvailable ? contactId || "" : ""
  );
  const hasEligibleApplications = (eligibleApplications?.length ?? 0) > 0;
  const canonicalApplicationId = useMemo(
    () => mostRecentApplicationId(eligibleApplications),
    [eligibleApplications]
  );

  const { data: contact } = useCrmContact(contactId || "");
  // Invited but not started: a different tooltip and no Invite button.
  const invitedNotStarted = useMemo(
    () => hasOutstandingInvite(contact),
    [contact]
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const handleInviteToApply = useCallback(() => setInviteOpen(true), []);
  // Only fetched while the modal is open — the lead is not needed otherwise.
  const { data: inviteLead } = useCrmLeadDetails(
    inviteOpen && options?.inviteLeadId ? String(options.inviteLeadId) : ""
  );

  // Invite-to-apply modal, pre-filled from the CRM contact.
  const inviteModal = (
    <Show if={inviteOpen}>
      <InviteAndCreateModal
        open={inviteOpen}
        setOpen={setInviteOpen}
        leadInfo={
          contact
            ? {
                first_name: contact.first_name,
                last_name: contact.last_name,
                primary_email: contact.emails?.[0] ?? ""
              }
            : undefined
        }
        zIndex={options?.inviteModalZIndex}
        leadId={options?.inviteLeadId}
        propertyId={inviteLead?.property_id || undefined}
        propertyName={inviteLead?.property?.name}
        unitId={inviteLead?.unit_id || undefined}
        unitName={inviteLead?.unit?.title}
      />
    </Show>
  );

  const visibleTabs = useMemo(
    () =>
      composerTabs
        .filter((tab) => {
          if (tab.value === "sms") return type.isCommunicationSms;
          if (tab.value === "email") return type.isCommunicationEmail;
          if (tab.value === "voice") return isVoiceEnabled;
          if (tab.value === "chat") return chatTabAvailable;
          if (tab.value === "note") return type.isBoomCRM;
          return true;
        })
        .map((tab) =>
          tab.value === "chat" && !hasEligibleApplications
            ? {
                ...tab,
                disabled: true,
                disabledTooltip: (
                  <ChatDisabledTooltip
                    invitedNotStarted={invitedNotStarted}
                    onInvite={handleInviteToApply}
                  />
                )
              }
            : tab
        ),
    [
      type.isCommunicationSms,
      type.isCommunicationEmail,
      type.isBoomCRM,
      isVoiceEnabled,
      chatTabAvailable,
      hasEligibleApplications,
      invitedNotStarted,
      handleInviteToApply
    ]
  );

  return {
    visibleTabs,
    chatTabAvailable,
    hasEligibleApplications,
    canonicalApplicationId,
    inviteModal
  };
};
